import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Header scroll effect (sections/shugar-header-scroll.liquid).
 *
 * Toggles [data-shugar-compact] on Horizon's #header-component once the page
 * has scrolled past a threshold; the section's CSS does the shrinking.
 *
 * - Uses Horizon's scroll-container helpers because on desktop the page
 *   scrolls inside .page-wrapper, not the window.
 * - Enter and exit points differ (hysteresis): the header losing height moves
 *   the page, and a single threshold would let it bounce back across and
 *   flicker between sizes.
 * - Looks the header and settings up on every check, so theme-editor
 *   re-renders of either section keep working (a module only runs once).
 */
const EXIT_AT = 20;
const DEFAULT_ENTER_AT = 200;
const DEFAULT_DURATION = 600;
// Same curve as --shugar-header-easing in the section's CSS: quick start, gentle settle.
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

let frame = null;

function update() {
  frame = null;

  const header = document.getElementById('header-component');
  const config = document.querySelector('[data-shugar-header-scroll]');
  if (!header) return;

  if (!(config instanceof HTMLElement)) {
    header.removeAttribute('data-shugar-compact');
    return;
  }

  const enterAt = Number(config.dataset.enterAt) || DEFAULT_ENTER_AT;
  const duration = Number(config.dataset.duration) || DEFAULT_DURATION;
  const scrollTop = getScrollTop();
  const isCompact = header.hasAttribute('data-shugar-compact');

  if (!isCompact && scrollTop > enterAt) {
    setCompact(header, true, duration);
  } else if (isCompact && scrollTop < EXIT_AT) {
    setCompact(header, false, duration);
  }
}

/**
 * Toggles the compact state and slides the logo and the menu to their new
 * spots (FLIP). The section's CSS moves them instantly — the logo column swaps
 * grid areas and the menu row lifts into the logo row — so measure each before
 * and after and animate the difference away.
 * @param {HTMLElement} header
 * @param {boolean} compact
 * @param {number} duration - Milliseconds, matching the section's CSS transitions
 */
function setCompact(header, compact, duration) {
  const moving = [
    header.querySelector('.header__row--top .header__column--center'),
    header.querySelector('.header__row--bottom header-menu'),
  ].filter((element) => element instanceof HTMLElement);
  const before = moving.map((element) => element.getBoundingClientRect());

  header.toggleAttribute('data-shugar-compact', compact);

  if (reducedMotion.matches) return;

  moving.forEach((element, index) => {
    const after = element.getBoundingClientRect();
    const x = before[index].left - after.left;
    const y = before[index].top - after.top;
    if (Math.abs(x) < 1 && Math.abs(y) < 1) return;

    element.animate([{ transform: `translate(${x}px, ${y}px)` }, { transform: 'none' }], {
      duration,
      easing: EASING,
    });
  });
}

function onScroll() {
  if (frame === null) frame = requestAnimationFrame(update);
}

let scrollTarget = getScrollEventTarget();
scrollTarget.addEventListener('scroll', onScroll, { passive: true });

// Desktop and mobile scroll different elements; rebind when the viewport crosses that breakpoint.
const mediaQuery =
  typeof scrollContainerMediaQuery === 'string' ? window.matchMedia(scrollContainerMediaQuery) : scrollContainerMediaQuery;

mediaQuery?.addEventListener?.('change', () => {
  scrollTarget.removeEventListener('scroll', onScroll);
  scrollTarget = getScrollEventTarget();
  scrollTarget.addEventListener('scroll', onScroll, { passive: true });
  update();
});

document.addEventListener('shopify:section:load', update);
update();
